import { useEffect, useRef, useState, useCallback } from 'react'

export interface SwipeGestureConfig {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  onSwipeUp?: () => void
  onSwipeDown?: () => void
  threshold?: number // Minimum distance for swipe (default: 50px)
  velocityThreshold?: number // Minimum velocity (default: 0.3)
}

export interface SwipeGestureState {
  isSwiping: boolean
  direction: 'left' | 'right' | 'up' | 'down' | null
  distance: number
}

/**
 * Custom hook for swipe gesture detection
 * Supports left, right, up, and down swipes with configurable thresholds
 */
export const useSwipeGesture = (config: SwipeGestureConfig) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    velocityThreshold = 0.3,
  } = config

  const [state, setState] = useState<SwipeGestureState>({
    isSwiping: false,
    direction: null,
    distance: 0,
  })

  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null)
  const touchEnd = useRef<{ x: number; y: number; time: number } | null>(null)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchEnd.current = null
    touchStart.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
      time: Date.now(),
    }
    setState({ isSwiping: true, direction: null, distance: 0 })
  }, [])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!touchStart.current) return

    const currentX = e.targetTouches[0].clientX
    const currentY = e.targetTouches[0].clientY
    const diffX = touchStart.current.x - currentX
    const diffY = touchStart.current.y - currentY

    // Determine primary direction
    let direction: 'left' | 'right' | 'up' | 'down' | null = null
    let distance = 0

    if (Math.abs(diffX) > Math.abs(diffY)) {
      // Horizontal swipe
      direction = diffX > 0 ? 'left' : 'right'
      distance = Math.abs(diffX)
    } else {
      // Vertical swipe
      direction = diffY > 0 ? 'up' : 'down'
      distance = Math.abs(diffY)
    }

    setState({ isSwiping: true, direction, distance })
  }, [])

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!touchStart.current) return

      touchEnd.current = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
        time: Date.now(),
      }

      const diffX = touchStart.current.x - touchEnd.current.x
      const diffY = touchStart.current.y - touchEnd.current.y
      const timeDiff = touchEnd.current.time - touchStart.current.time
      const velocity = Math.sqrt(diffX * diffX + diffY * diffY) / timeDiff

      // Check if swipe meets threshold requirements
      if (velocity < velocityThreshold) {
        setState({ isSwiping: false, direction: null, distance: 0 })
        return
      }

      // Determine swipe direction and trigger callback
      if (Math.abs(diffX) > Math.abs(diffY)) {
        // Horizontal swipe
        if (Math.abs(diffX) > threshold) {
          if (diffX > 0 && onSwipeLeft) {
            onSwipeLeft()
          } else if (diffX < 0 && onSwipeRight) {
            onSwipeRight()
          }
        }
      } else {
        // Vertical swipe
        if (Math.abs(diffY) > threshold) {
          if (diffY > 0 && onSwipeUp) {
            onSwipeUp()
          } else if (diffY < 0 && onSwipeDown) {
            onSwipeDown()
          }
        }
      }

      setState({ isSwiping: false, direction: null, distance: 0 })
      touchStart.current = null
      touchEnd.current = null
    },
    [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold, velocityThreshold]
  )

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    state,
  }
}

/**
 * Hook for pull-to-refresh gesture
 */
export const usePullToRefresh = (onRefresh: () => void | Promise<void>, threshold = 80) => {
  const [isPulling, setIsPulling] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const touchStart = useRef<number | null>(null)
  const scrollTop = useRef<number>(0)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    scrollTop.current = window.pageYOffset || document.documentElement.scrollTop
    if (scrollTop.current === 0) {
      touchStart.current = e.touches[0].clientY
    }
  }, [])

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (touchStart.current === null || scrollTop.current > 0) return

      const currentY = e.touches[0].clientY
      const distance = currentY - touchStart.current

      if (distance > 0) {
        setIsPulling(true)
        setPullDistance(Math.min(distance, threshold * 1.5))
      }
    },
    [threshold]
  )

  const handleTouchEnd = useCallback(async () => {
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true)
      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
      }
    }
    setIsPulling(false)
    setPullDistance(0)
    touchStart.current = null
  }, [pullDistance, threshold, onRefresh, isRefreshing])

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isPulling,
    pullDistance,
    isRefreshing,
    progress: Math.min((pullDistance / threshold) * 100, 100),
  }
}

/**
 * Hook for long press gesture
 */
export const useLongPress = (
  onLongPress: () => void,
  { delay = 500, shouldPreventDefault = true } = {}
) => {
  const [isLongPressing, setIsLongPressing] = useState(false)
  const timerRef = useRef<NodeJS.Timeout>()
  const isLongPressRef = useRef(false)

  const start = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (shouldPreventDefault) {
        e.preventDefault()
      }

      isLongPressRef.current = false
      setIsLongPressing(true)

      timerRef.current = setTimeout(() => {
        isLongPressRef.current = true
        onLongPress()
      }, delay)
    },
    [onLongPress, delay, shouldPreventDefault]
  )

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    setIsLongPressing(false)
  }, [])

  return {
    handlers: {
      onMouseDown: start,
      onMouseUp: clear,
      onMouseLeave: clear,
      onTouchStart: start,
      onTouchEnd: clear,
    },
    isLongPressing,
  }
}
